import * as React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { AUB, BG_SOFT, INK, INK2, LINE } from "../chat/chatTheme";

/**
 * Vollflächen-Overlay über der App: füllt Breite und Höhe der App-Fläche aus
 * und ersetzt für die Dauer einer Aufgabe die Seite darunter. Gedacht für
 * Inhalte, die den ganzen Platz brauchen und einen klaren Abschluss haben —
 * geschlossen wird über das Kreuz, über „Fertig" oder mit ESC.
 *
 * Bewusst KEIN Fenster des WindowManagers: ein Fenster ist ein Geschwister
 * aller anderen Fenster und wandert beim Anklicken nach vorn — ein großes
 * Fenster begräbt damit die kleinen Dialoge, die es selbst geöffnet hat. Das
 * Overlay hat keinen Fokus-Vorrang und kann das nicht.
 *
 * ── Wo das Overlay in der z-index-Leiter steht (und warum so tief) ─────────
 * Die App stapelt so (Master-Leiter: `windowManager/zLayers.ts`):
 *
 *   Seite, Sidebar, Topbar, Bottom-Leiste .............. ≤ 1100
 *   → App-Overlay (HIER) ............................... 1150
 *   Sheets/Scrims/Popover der Seite .................... 1200 – 2100
 *     (.tp-dp-sheet-scrim 1200, .kvp-rd-scrim 1400, Termin-Peek 1890,
 *      Termin-Kontextmenü 1900, RdPopover 2000, RdContextMenu 2100)
 *   Fenster-Layer des WindowManagers ................... 9000
 *   Dialoge über Fenstern (.cal-vgd-*) ................. 9600
 *   Dock / WindowTaskbar ............................... 99999
 *   Modals über Fenstern (Z_LAYERS.ueberFenster) ....... 100000
 *   Toasts, Anrufleiste, Verwerfen-Rückfrage ........... ab 100000
 *
 * Das Overlay sitzt absichtlich auf der UNTERSTEN Sprosse über der Seite: es
 * deckt Seite und Navigation ab, liegt aber unter allem, was aus ihm heraus
 * geöffnet wird. Dadurch landet jeder Dialog, Popover, Sheet und jedes
 * Termin-Fenster aus dem Overlay automatisch DAVOR — ohne eigenen z-index und
 * ohne Wettrüsten. Wer hier einen neuen Dialog einhängt, muss nichts tun; er
 * reiht sich von selbst ein. Ein höherer Wert für das Overlay würde genau das
 * zerstören: die eigenen Kinder verschwänden dahinter.
 *
 * Grenze: der Fenster-Layer (9000) ist ein eigener Stacking-Context. Wird das
 * Overlay aus einem schwebenden Fenster heraus geöffnet, liegt dieses Fenster
 * darüber. Die Leiter dafür umzubauen wäre falsch — ein höheres Overlay
 * begräbt seine eigenen Dialoge. Der Aufrufer schickt das Fenster stattdessen
 * für die Dauer der Aufgabe in die Taskbar und holt es danach zurück; so macht
 * es der Reiter „Termine" (`sidebar/TermineTab.tsx`, Prop `fensterId`). Wer das
 * Overlay aus einem Fenster heraus einhängt, muss dasselbe tun.
 */
export const Z_APP_OVERLAY = 1150;

const huelleStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: Z_APP_OVERLAY,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    background: "#fff",
    outline: "none",
};

const kopfStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flex: "0 0 auto",
    padding: "10px 14px",
    borderBottom: `1px solid ${LINE}`,
    background: BG_SOFT,
};

const titelBlockStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    flex: 1,
    minWidth: 0,
};

const titelStyle: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 700,
    color: INK,
};

const hinweisStyle: React.CSSProperties = {
    fontSize: 12,
    color: INK2,
    lineHeight: 1.35,
};

const fertigStyle: React.CSSProperties = {
    flex: "0 0 auto",
    minHeight: 34,
    padding: "0 18px",
    border: "none",
    borderRadius: 8,
    background: AUB,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: "pointer",
};

const kreuzStyle: React.CSSProperties = {
    flex: "0 0 auto",
    width: 34,
    height: 34,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: `1px solid ${LINE}`,
    borderRadius: 8,
    background: "#fff",
    color: INK2,
    fontSize: 16,
    lineHeight: 1,
    fontFamily: "inherit",
    cursor: "pointer",
};

const inhaltStyle: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
};

export type AppFlaechenOverlayProps = {
    titel: string;
    /** Einzeiler unter dem Titel — sagt, was hier zu tun ist. */
    hinweis?: string;
    /** Beschriftung des abschließenden Knopfes. */
    fertigText?: string;
    /** Kreuz, „Fertig" und ESC rufen alle dasselbe. */
    onSchliessen: () => void;
    children: React.ReactNode;
};

export const AppFlaechenOverlay: React.FC<AppFlaechenOverlayProps> = ({
    titel,
    hinweis,
    fertigText = "Fertig",
    onSchliessen,
    children,
}) => {
    const huelleRef = React.useRef<HTMLDivElement | null>(null);

    // Fokus in das Overlay holen: sonst hinge die Tastatur weiter an der Seite
    // darunter und ESC käme hier nie an.
    React.useEffect(() => {
        huelleRef.current?.focus({ preventScroll: true });
    }, []);

    const beiTaste = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key !== "Escape" || e.defaultPrevented) return;
        // ESC darf das Overlay unter KEINEN Umständen verlassen: unter der Fläche
        // hängen weitere ESC-Listener am window (u. a. die Mail-Kontaktleiste),
        // die sonst mit zugehen — sichtbar wird das erst nach „Fertig".
        e.stopPropagation();
        // In einem Eingabefeld gehört ESC dem Feld (Suche leeren, Vorschlagsliste
        // schließen) — sonst risse ein Tastendruck beim Tippen alles zu. Das Feld
        // hat den Tastendruck vor uns gesehen; ab hier ist er nur noch im Weg.
        const ziel = e.target as HTMLElement | null;
        const tag = ziel?.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || ziel?.isContentEditable) {
            return;
        }
        onSchliessen();
    };

    return createPortal(
        <div
            ref={huelleRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={titel}
            style={huelleStyle}
            onKeyDown={beiTaste}
        >
            <div style={kopfStyle}>
                <div style={titelBlockStyle}>
                    <span style={titelStyle}>{titel}</span>
                    {hinweis && <span style={hinweisStyle}>{hinweis}</span>}
                </div>
                <button type="button" style={fertigStyle} onClick={onSchliessen}>
                    {fertigText}
                </button>
                <button
                    type="button"
                    style={kreuzStyle}
                    onClick={onSchliessen}
                    aria-label="Schließen"
                    title="Schließen (Esc)"
                >
                    ✕
                </button>
            </div>
            <div style={inhaltStyle}>{children}</div>
        </div>,
        document.body,
    );
};

export default AppFlaechenOverlay;
